'use client';

import { type ReactNode } from 'react';

import { cn } from '@ps/ui/utils';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  Strikethrough,
  TextQuote,
} from 'lucide-react';
import { useSlate } from 'slate-react';

import type { CustomEditor } from './types/SlateNodes';
import {
  type BlockFormat,
  type MarkKey,
  isAlignType,
  isBlockActive,
  isMarkActive,
  toggleBlock,
  toggleMark,
} from './utils/slateFormat';

/**
 * Composer toolbar — Bold / Italic / Strikethrough · 4 alignments ·
 * Block-quote / Numbered-list / Bulleted-list. Must live as a child
 * of `<Slate>` so `useSlate()` resolves to the active editor.
 *
 * Active state is read live from the editor on every render — Slate
 * forces a parent re-render on each change, so each button reflects
 * the cursor's current marks / block type without manual wiring.
 */
export function ChannelComposerToolbar() {
  return (
    <div className="flex flex-wrap items-center gap-1 px-1 py-1">
      <MarkButton format="bold" label="Bold">
        <Bold className="h-4 w-4" aria-hidden />
      </MarkButton>
      <MarkButton format="italic" label="Italic">
        <Italic className="h-4 w-4" aria-hidden />
      </MarkButton>
      <MarkButton format="strikethrough" label="Strikethrough">
        <Strikethrough className="h-4 w-4" aria-hidden />
      </MarkButton>
      <Divider />
      <BlockButton format="left" label="Align left">
        <AlignLeft className="h-4 w-4" aria-hidden />
      </BlockButton>
      <BlockButton format="center" label="Align center">
        <AlignCenter className="h-4 w-4" aria-hidden />
      </BlockButton>
      <BlockButton format="right" label="Align right">
        <AlignRight className="h-4 w-4" aria-hidden />
      </BlockButton>
      <BlockButton format="justify" label="Justify">
        <AlignJustify className="h-4 w-4" aria-hidden />
      </BlockButton>
      <Divider />
      <BlockButton format="block-quote" label="Block quote">
        <TextQuote className="h-4 w-4" aria-hidden />
      </BlockButton>
      <BlockButton format="numbered-list" label="Numbered list">
        <ListOrdered className="h-4 w-4" aria-hidden />
      </BlockButton>
      <BlockButton format="bulleted-list" label="Bulleted list">
        <List className="h-4 w-4" aria-hidden />
      </BlockButton>
    </div>
  );
}

function MarkButton({
  format,
  label,
  children,
}: {
  format: MarkKey;
  label: string;
  children: ReactNode;
}) {
  // `useSlate()` returns Slate's `BaseEditor`; cast to the
  // composer's editor shape (workspace-wide augmentation lives in
  // another slice). Safe because the editor was built with
  // `withReact(withHistory(createEditor()))`.
  const editor = useSlate() as unknown as CustomEditor;
  const active = isMarkActive(editor, format);
  return (
    <ToolButton
      label={label}
      active={active}
      onMouseDown={event => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      {children}
    </ToolButton>
  );
}

function BlockButton({
  format,
  label,
  children,
}: {
  format: BlockFormat;
  label: string;
  children: ReactNode;
}) {
  // `useSlate()` returns Slate's `BaseEditor`; cast to the
  // composer's editor shape (workspace-wide augmentation lives in
  // another slice). Safe because the editor was built with
  // `withReact(withHistory(createEditor()))`.
  const editor = useSlate() as unknown as CustomEditor;
  const active = isBlockActive(
    editor,
    format,
    isAlignType(format) ? 'align' : 'type',
  );
  return (
    <ToolButton
      label={label}
      active={active}
      onMouseDown={event => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      {children}
    </ToolButton>
  );
}

function ToolButton({
  active,
  label,
  onMouseDown,
  children,
}: {
  active: boolean;
  label: string;
  onMouseDown: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onMouseDown={onMouseDown}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded transition',
        active
          ? 'text-content-1 bg-surface-3'
          : 'hover:text-content-1 text-content-3 hover:bg-surface-1',
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="bg-surface-2 mx-1 h-4 w-px" aria-hidden />;
}
